"use client";

import React, { useState } from 'react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import axios from 'axios';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
    // Clear previous messages when user starts typing
    if (message) {
      setMessage('');
      setMessageType('');
    }
  };

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Validate email
    if (!email) {
      setMessage('Please enter your email address.');
      setMessageType('error');
      return;
    }

    if (!validateEmail(email)) {
      setMessage('Please enter a valid email address.');
      setMessageType('error');
      return;
    }

    // Start submission process
    setIsSubmitting(true);

    try {
      // Make API request to save the email
      const response = await axios.post('/api/newsletter', { email });

      // Email saved successfully
      setMessage('Thank you for subscribing! You will soon receive our updates.');
      setMessageType('success');
      setEmail(''); // Clear field after success
    } catch (error: any) {
      console.error('Error submitting form:', error);
      // Handle different error types
      if (error.response) {
        // Server responded with an error
        if (error.response.status === 409) {
          setMessage('This email is already subscribed to our newsletter.');
        } else {
          setMessage(error.response.data.error || 'There was an error processing your request. Please try again.');
        }
      } else {
        setMessage('We couldn\'t process your request. Please try again later.');
      }
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-100 border-t shadow-sm">
      <div className="container mx-auto px-6 py-12">
        {/* Main sections */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm text-gray-700">
          {/* About */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Be Underwater</h3>
            <p>
              The best platform to discover and compare scuba diving courses. Join our community and explore the underwater world.
            </p>
          </div>

          {/* Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Info</h3>
            <ul className="space-y-2">
              <li><a href="/about" className="hover:text-gray-900">About</a></li>
              <li><a href="/privacy" className="hover:text-gray-900">Privacy</a></li>
              <li><a href="/terms" className="hover:text-gray-900">Terms</a></li>
              <li><a href="/user-data-deletion" className="hover:text-gray-900">User Data Deletion</a></li>
              <li><a href="/faq" className="hover:text-gray-900">FAQs</a></li>
              <li><a href="mailto:beunderwaterapp@gmail.com" className="hover:text-gray-900">Contact</a></li>
              <li><a href="/articles/scuba-diving-articles" className="hover:text-gray-900">Scuba diving articles</a></li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-lg font-semibold mb-4">For Dive Centers</h3>
            <ul className="space-y-2">
              <li><a href="/registercenter" className="hover:text-gray-900">Register Your Center</a></li>
              <li><a href="/understandingpayout" className="hover:text-gray-900">Understanding Payouts</a></li>
            </ul>

            <div className="flex space-x-4 mt-4">
              <a href="https://instagram.com/beunderwaterapp" target="_blank" rel="noreferrer" className="text-gray-500 hover:text-cyan-500">
                <FaInstagram size={20} />
              </a>
              <a href="https://www.tiktok.com/@beunderwaterapp" target="_blank" rel="noreferrer" className="text-gray-500 hover:text-black">
                <FaTiktok size={20} />
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Subscribe</h3>
            <p className="mb-4">Get the latest news and updates on the best diving destinations.</p>
            <form className="flex flex-col" onSubmit={handleSubmit}>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-grow px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none"
                  value={email}
                  onChange={handleEmailChange}
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  className={`px-4 py-2 bg-cyan-600 text-white rounded-r-md hover:bg-cyan-700 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Subscribe'}
                </button>
              </div>
              {message && (
                <p className={`mt-2 text-sm ${messageType === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t mt-8 pt-4">
          {/* Copyright */}
          <div className="text-center text-sm text-gray-600">
            © {new Date().getFullYear()} Be Underwater. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;